import { Pause, Play } from "lucide-react";
import React from "react";

import { useAudioVisualizer } from "./use-audio-visualizer";
import { cn } from "~/util/cn";

export const CenteredBarsVisualizer: React.FC<{
  visualData: number[];
  className?: string;
  minBrightness?: number;
  maxBrightness?: number;
  sliceLength?: number;
}> = ({
  visualData,
  className = "h-8",
  minBrightness = 90,
  maxBrightness = 130,
  sliceLength = 50,
}) => {
  return (
    <div
      className={cn(
        "flex items-center justify-end space-x-1 overflow-x-hidden rounded-lg",
        className,
      )}
    >
      {visualData.slice(-sliceLength).map((level, index) => {
        const brightness =
          minBrightness + (level / 100) * (maxBrightness - minBrightness);

        return (
          <div
            key={index}
            className="min-h-1.5 w-1 flex-shrink-0 rounded-b-lg rounded-t-lg"
            style={{
              height: `${level}%`,
              backgroundColor: "var(--fallback-p,oklch(var(--p)))",
              filter: `brightness(${brightness}%)`,
            }}
          />
        );
      })}
    </div>
  );
};

type Base64AudioVisualizerProps = {
  base64Audio: string;
  className?: string;
  minBrightness?: number;
  maxBrightness?: number;
  trackOpacity?: number;
  bars?: number;
};

export const Base64AudioVisualizer: React.FC<Base64AudioVisualizerProps> = ({
  base64Audio,
  className,
  minBrightness = 90,
  maxBrightness = 130,
  trackOpacity = 0.6,
  bars = 50,
}) => {
  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    audioData,
    togglePlay,
    seekAudio,
    currentPosition,
    formatTime,
  } = useAudioVisualizer(base64Audio, {
    bars,
  });

  return (
    <div
      className={cn(
        "grid grid-cols-[2rem_auto_4rem] items-center gap-2",
        className,
      )}
    >
      <audio ref={audioRef} src={base64Audio} preload="metadata" />

      <button onClick={togglePlay} className="btn-neutral btn-sm btn h-10 w-12">
        {isPlaying ? <Pause width={16} /> : <Play width={16} />}
      </button>

      <div
        className="h-8 w-full cursor-pointer overflow-hidden rounded-lg"
        onClick={seekAudio}
      >
        <div className="flex h-full items-center gap-0.5 sm:gap-1">
          {audioData.map((level, index) => {
            const brightness =
              minBrightness + (level / 100) * (maxBrightness - minBrightness);
            const active = index <= currentPosition;

            return (
              <div
                key={index}
                className="min-h-1.5 w-1 flex-shrink-0 rounded-b-lg rounded-t-lg"
                style={{
                  height: `${level}%`,
                  backgroundColor: active
                    ? "var(--fallback-p,oklch(var(--p)))"
                    : `var(--fallback-n,oklch(var(--n) / ${trackOpacity}))`,
                  filter: `brightness(${brightness}%)`,
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center gap-[1px] text-xs font-medium text-primary">
        <span>{formatTime(currentTime)}</span>
        <span>{"•"}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};
